import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CONTRACT_TYPE_LABELS, MANDATE_STATUS_LABELS } from '@aimmo/contracts';
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorState,
  Skeleton,
  resourceKeys,
} from '@aimmo/ui';
import { api, assertOk } from '../lib/api.js';
import { formatPrice } from '../lib/format.js';
import { MANDATE_STATUS_TONES } from './mandate-status.js';

/**
 * A property's mandates, newest first — the history that splitting the two
 * tables buys (docs/domain/06-mandates.md): re-listings, an owner change, a
 * sale that became a rental, all against the same asset.
 *
 * It reads through the mandates resource's own query key, so closing a mandate
 * elsewhere refreshes this list too.
 */
export function PropertyMandates({ propertyId }: { propertyId: string }) {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: [...resourceKeys.lists('mandates'), { propertyId }],
    queryFn: async ({ signal }) =>
      assertOk(
        await api.mandates.list({
          query: { page: 1, pageSize: 20, order: 'desc', sort: 'createdAt', propertyId },
          fetchOptions: { signal },
        }),
      ).body,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Incarichi</CardTitle>
      </CardHeader>
      <CardContent>
        {isPending && <Skeleton className="h-16 w-full" />}

        {isError && <ErrorState onRetry={() => void refetch()} />}

        {!isPending && !isError && data.items.length === 0 && (
          <p className="text-secondary text-muted">
            Nessun incarico su questo immobile. Un incarico lo lega al proprietario che lo affida
            all’agenzia.
          </p>
        )}

        <ul className="grid gap-2">
          {data?.items.map((mandate) => (
            <li key={mandate.id}>
              <Link
                to="/incarichi/$id"
                params={{ id: mandate.id }}
                className="flex flex-wrap items-center gap-3 rounded-control border border-border px-3 py-2 hover:bg-surface-sunken"
              >
                <Badge tone={MANDATE_STATUS_TONES[mandate.status]}>
                  {MANDATE_STATUS_LABELS[mandate.status]}
                </Badge>
                <span className="text-body text-ink">
                  {CONTRACT_TYPE_LABELS[mandate.contractType]}
                </span>
                <span className="text-body text-ink-2">{mandate.ownerName}</span>
                <span className="ml-auto text-body text-ink">{formatPrice(mandate.price)}</span>
                <span className="text-secondary text-muted">
                  {mandate.signedAt ?? mandate.createdAt.slice(0, 10)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
